import { ApiBase, HttpMethod } from "../../../../core/api-base";
import { z } from "zod";
import { GetSearchMenu200Response } from "../../../../api/generated/search.zod";
import {
    CreateReviewConfigBody,
    CreateReviewQuestionBody,
    GetReviewConfigs200Response,
    GetReviewConfigsWithQuestions200Response,
    GetReviewQuestions200Response,
    UpdateReviewQuestionBody,
} from "../../../../api/generated/reviews.zod";

type RequestPayload = Record<string, unknown> | FormData | string | null;
type SearchMenuResponse = z.infer<typeof GetSearchMenu200Response>;
type ReviewConfigsResponse = z.infer<typeof GetReviewConfigs200Response>;
type ReviewConfigsWithQuestionsResponse = z.infer<typeof GetReviewConfigsWithQuestions200Response>;
type ReviewQuestionsResponse = z.infer<typeof GetReviewQuestions200Response>;
type CreateReviewQuestionRequest = z.infer<typeof CreateReviewQuestionBody>;
type CreateReviewConfigRequest = z.infer<typeof CreateReviewConfigBody>;
type UpdateReviewQuestionRequest = z.infer<typeof UpdateReviewQuestionBody>;

function ensureOk(response: Response, context: string): void {
    if (!response.ok) {
        throw new Error(`${context} failed: HTTP ${response.status}`);
    }
}

export class CategoryReviewEditorApi extends ApiBase {
    public async fetchSearchMenu(): Promise<SearchMenuResponse> {
        const response = await super.apiGet("/api/v1/search/menu", { noSpinner: true });
        ensureOk(response, "fetchSearchMenu");
        return (await response.json()) as SearchMenuResponse;
    }

    public async fetchReviewConfigs(): Promise<ReviewConfigsResponse> {
        const response = await super.apiGet("/api/v1/reviews/configs", { noSpinner: true });
        ensureOk(response, "fetchReviewConfigs");
        return (await response.json()) as ReviewConfigsResponse;
    }

    public async fetchReviewConfigsWithQuestions(): Promise<ReviewConfigsWithQuestionsResponse> {
        const response = await super.apiGet("/api/v1/reviews/configs/with-questions", { noSpinner: true });
        ensureOk(response, "fetchReviewConfigsWithQuestions");
        return (await response.json()) as ReviewConfigsWithQuestionsResponse;
    }

    public async fetchReviewQuestions(configId: number): Promise<ReviewQuestionsResponse> {
        const response = await super.apiGet(`/api/v1/reviews/configs/${configId}/questions`, {
            noSpinner: true,
        });
        ensureOk(response, "fetchReviewQuestions");
        return (await response.json()) as ReviewQuestionsResponse;
    }

    public async createCategory(payload: RequestPayload): Promise<Response> {
        const response = await super.apiPost("/api/v1/categories", payload, { noSpinner: true });
        ensureOk(response, "createCategory");
        return response;
    }

    public async updateCategory(categoryId: number, payload: RequestPayload): Promise<Response> {
        const response = await super.apiRequest(`/api/v1/categories/${categoryId}`, HttpMethod.Put, payload, {
            noSpinner: true,
        });
        ensureOk(response, "updateCategory");
        return response;
    }

    public async deleteCategory(categoryId: number): Promise<Response> {
        const response = await super.apiDelete(`/api/v1/categories/${categoryId}`, {
            noSpinner: true,
        });
        ensureOk(response, "deleteCategory");
        return response;
    }

    public async createReviewQuestion(configId: number, payload: CreateReviewQuestionRequest): Promise<Response> {
        const response = await super.apiPost(`/api/v1/reviews/configs/${configId}/questions`, payload, {
            noSpinner: true,
        });
        ensureOk(response, "createReviewQuestion");
        return response;
    }

    public async createReviewConfig(payload: CreateReviewConfigRequest): Promise<Response> {
        const response = await super.apiPost("/api/v1/reviews/configs", payload, {
            noSpinner: true,
        });
        ensureOk(response, "createReviewConfig");
        return response;
    }

    public async updateReviewQuestion(
        configId: number,
        questionId: number,
        payload: UpdateReviewQuestionRequest,
    ): Promise<Response> {
        const response = await super.apiRequest(
            `/api/v1/reviews/configs/${configId}/questions/${questionId}`,
            HttpMethod.Put,
            payload,
            { noSpinner: true },
        );
        ensureOk(response, "updateReviewQuestion");
        return response;
    }

    public async deleteReviewQuestion(configId: number, questionId: number): Promise<Response> {
        const response = await super.apiDelete(`/api/v1/reviews/configs/${configId}/questions/${questionId}`, {
            noSpinner: true,
        });
        ensureOk(response, "deleteReviewQuestion");
        return response;
    }
}
